import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Offcanvas, Card, Button, ListGroup } from 'react-bootstrap';

import FSCAddClauseItemSidebar from './FSCAddClauseItemSidebar';
import { bulkAddFSCControls, fetchFSCControlTree } from '../../../../store/slices/fscComplianceSlice';

import toastAlert from '../../../../js/Base/ToastAlert';

function buildPathLabel(pathLabels = []) {
  const list = (pathLabels || []).filter(Boolean);
  return list.length > 0 ? list.join(' / ') : '(未選擇階層)';
}

export default function FSCAddClauseSidebar({ show, onHide, projectUuid, cloud, cloudScope, tree }) {
  const dispatch = useDispatch();

  const [drafts, setDrafts] = useState([]);
  const [showItemSidebar, setShowItemSidebar] = useState(false);

  // 編輯草稿用
  const [editingIndex, setEditingIndex] = useState(null);

  const rootOptions = useMemo(() => {
    return (tree || []).map((n) => ({
      key: n.control_id,
      label: `${n.control_id || ''} ${n.control_name || ''}`.trim(),
      node: n,
    }));
  }, [tree]);

  const handleClose = () => {
    setDrafts([]);
    setEditingIndex(null);
    onHide();
  };

  const removeDraft = (idx) => {
    setDrafts((prev) => prev.filter((_, i) => i !== idx));
    if (editingIndex === idx) setEditingIndex(null);
  };

  const openCreate = () => {
    setEditingIndex(null);
    setShowItemSidebar(true);
  };

  const openEdit = (idx) => {
    setEditingIndex(idx);
    setShowItemSidebar(true);
  };

  const handleSubmitDraft = (draftItem) => {
    if (editingIndex === null || editingIndex === undefined) {
      setDrafts((prev) => [...prev, draftItem]);
    } else {
      setDrafts((prev) => prev.map((d, i) => (i === editingIndex ? draftItem : d)));
      setEditingIndex(null);
    }
    setShowItemSidebar(false);
  };

  const editingDraft = useMemo(() => {
    if (editingIndex === null || editingIndex === undefined) return null;
    return drafts[editingIndex] || null;
  }, [drafts, editingIndex]);

  // 把 drafts 的 custom_nodes 展開成 bulk payload
  const buildBulkControlsPayload = () => {
    // 1) 蒐集所有 pending nodes（去重）
    const nodeMap = new Map();
    (drafts || []).forEach((d) => {
      const nodes = Array.isArray(d.custom_nodes) ? d.custom_nodes : [];
      nodes.forEach((n) => {
        if (!n || !n.client_key || !n.control_name) return;
        const key = String(n.client_key);
        if (!nodeMap.has(key)) {
          nodeMap.set(key, {
            client_key: key,
            control_name: String(n.control_name),
            parent_control_id: n.parent_control_id ? String(n.parent_control_id) : null,
            depth: Number(n.depth) || 0,
          });
        }
      });
    });

    const customControls = Array.from(nodeMap.values())
      .sort((a, b) => (a.depth || 0) - (b.depth || 0))
      .map((n) => {
        const item = {
          control_id: n.client_key, // client key（後端會 mapping，不會真的存進 DB）
          control_name: n.control_name,
          is_assessable: false,
          recommendation: '',
          remediation_steps: '',
        };
        if (n.parent_control_id) item.parent_control_id = n.parent_control_id;
        return item;
      });

    // 2) leaf controls（真正條款細項）
    const leafControls = (drafts || []).map((d) => ({
      parent_control_id: d.parent_control_id,
      control_name: d.control_name,
      recommendation: d.recommendation || '',
      remediation_steps: d.remediation_steps || '',
    }));

    return [...customControls, ...leafControls];
  };

  // 批次新增時必須帶 project_uuid
  const handleConfirmAll = async () => {
    try {
      if (!drafts || drafts.length === 0) return;

      if (!projectUuid) {
        toastAlert.autoClose(true).showError('project_uuid is required');
        return;
      }

      if (!cloud) {
        toastAlert.autoClose(true).showError('缺少 cloud 參數');
        return;
      }

      const controls = buildBulkControlsPayload();

      await dispatch(
        bulkAddFSCControls({
          project_uuid: projectUuid,
          cloud,
          cloud_scope: cloudScope,
          controls,
        })
      ).unwrap();

      // 成功後重新取得控制項樹
      await dispatch(fetchFSCControlTree({ project_uuid: projectUuid, cloud, cloud_scope: cloudScope })).unwrap();

      toastAlert.autoClose(true).showSuccess('新增成功');

      // 清空草稿並關閉
      setDrafts([]);
      setEditingIndex(null);
      onHide();
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.detail || err?.message || '新增條款失敗';
      toastAlert.autoClose(true).showError(msg);
    }
  };

  return (
    <>
      <Offcanvas show={show} onHide={handleClose} placement="end" style={{ width: '75%' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <h4 style={{ margin: 0 }}>新增條款</h4>
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
          }}
        >
          {/* 主要內容區 */}
          <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
            <Card>
              <Card.Header style={{ fontWeight: 'bold' }}>條款</Card.Header>
              <Card.Body>
                <Button variant="outline-secondary" size="sm" style={{ fontWeight: 'bold' }} onClick={openCreate}>
                  新增條款
                </Button>

                <div style={{ marginTop: 16 }}>
                  {drafts.length === 0 ? (
                    <div className="text-muted">尚未新增任何條款</div>
                  ) : (
                    <ListGroup>
                      {drafts.map((d, idx) => (
                        <ListGroup.Item key={`${d.temp_id}-${idx}`} className="d-flex justify-content-between">
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 'bold' }}>{d.control_name}</div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              {buildPathLabel(d.path_labels)}
                            </div>
                          </div>

                          <div className="d-flex" style={{ gap: 8 }}>
                            <Button
                              variant="text"
                              size="sm"
                              style={{ fontWeight: 'bold' }}
                              onClick={() => openEdit(idx)}
                            >
                              編輯
                            </Button>
                            <Button
                              variant="text"
                              size="sm"
                              style={{ fontWeight: 'bold' }}
                              onClick={() => removeDraft(idx)}
                            >
                              刪除
                            </Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* Footer */}
          <div
            style={{
              borderTop: '1px solid #e5e5e5',
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              background: '#fff',
            }}
          >
            <Button className="btn btn-cancel" variant="secondary" onClick={handleClose}>
              取消
            </Button>
            <Button
              className="btn btn-custom"
              variant="success"
              onClick={handleConfirmAll}
              disabled={drafts.length === 0}
            >
              新增所有條款
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <FSCAddClauseItemSidebar
        show={showItemSidebar}
        onHide={() => {
          setShowItemSidebar(false);
          setEditingIndex(null);
        }}
        rootOptions={rootOptions}
        initialDraft={editingDraft}
        onSubmit={handleSubmitDraft}
      />
    </>
  );
}
